import { z } from "zod";
import { cidrContains, isPrivateCidr, parseIpv4, parseIpv4Cidr, type Ipv4Cidr } from "./ip.js";
import { Labels } from "./ssh-keys.js";

/** Smallest and largest private network a project can have (prefix lengths). */
export const NETWORK_PREFIX = { min: 8, max: 24 } as const;
/** A subnet may be as small as a /30 (two usable addresses besides gateway and broadcast). */
export const SUBNET_PREFIX_MAX = 30;
/** A server can be in at most this many private networks (Hetzner's limit). */
export const MAX_SERVER_NETWORKS = 3;
/**
 * Extra addresses (alias IPs) a server may take in one network. Our
 * assumption of the provider's limit; its own refusal still reaches the user.
 */
export const MAX_ALIAS_IPS = 5;

export const NetworkName = z.string().trim().min(1).max(128);

/**
 * An IPv4 block whose address is the first of the block (`10.0.0.0/24`).
 * The check aborts: Zod runs later refinements even after a failed one, and
 * those may rely on the value being a block (a malformed range once threw
 * there and answered 500 instead of 422).
 */
export const Ipv4Block = z
  .string()
  .trim()
  .refine((v) => parseIpv4Cidr(v) !== null, {
    message: "Not an IPv4 network such as 10.0.0.0/24",
    params: { reason: "ipv4_block_invalid" },
    abort: true,
  });

const Ipv4Address = z
  .string()
  .trim()
  .refine((v) => parseIpv4(v) !== null, {
    message: "Not an IPv4 address",
    params: { reason: "ipv4_address_invalid" },
    abort: true,
  });

const isNetworkRange = (cidr: Ipv4Cidr | null): boolean =>
  cidr !== null &&
  isPrivateCidr(cidr) &&
  cidr.prefix >= NETWORK_PREFIX.min &&
  cidr.prefix <= NETWORK_PREFIX.max;

/** The address range of a whole network: RFC 1918 only, /8 to /24. */
export const NetworkRange = Ipv4Block.refine((v) => isNetworkRange(parseIpv4Cidr(v)), {
  message: "Use a private range (10.0.0.0/8, 172.16.0.0/12 or 192.168.0.0/16), from /8 to /24",
  params: { reason: "network_range_not_private" },
});

/** `cloud` subnets hold cloud servers; the others connect dedicated servers (vSwitch). */
export const SubnetType = z.enum(["cloud", "server", "vswitch"]);
export type SubnetType = z.infer<typeof SubnetType>;

export const Subnet = z.object({
  type: SubnetType,
  ipRange: z.string(),
  /** Network zone (e.g. `eu-central`): servers of this zone can join the subnet. */
  zone: z.string(),
  /** The address the provider uses as the subnet's router. */
  gateway: z.string().nullable(),
});
export type Subnet = z.infer<typeof Subnet>;

/** Traffic for `destination` is sent to `gateway`, a server inside the network. */
export const NetworkRoute = z.object({ destination: z.string(), gateway: z.string() });
export type NetworkRoute = z.infer<typeof NetworkRoute>;

/** A server attached to the network, with its address in it. */
export const NetworkServer = z.object({ id: z.uuid(), name: z.string(), ip: z.string() });
export type NetworkServer = z.infer<typeof NetworkServer>;

/** A network as the provider adapter reports it (without our id and attachments). */
export const NetworkData = z.object({
  name: z.string(),
  ipRange: z.string(),
  subnets: z.array(Subnet),
  routes: z.array(NetworkRoute),
  labels: Labels,
  protection: z.object({ delete: z.boolean() }),
  createdAt: z.iso.datetime(),
});
export type NetworkData = z.infer<typeof NetworkData>;

export const Network = NetworkData.extend({
  id: z.uuid(),
  servers: z.array(NetworkServer),
});
export type Network = z.infer<typeof Network>;

/**
 * True unless both are blocks and `block` lies outside `range`: a malformed
 * value already has its own issue, and "outside" would only add noise.
 */
const insideOrMalformed = (range: string, block: string) => {
  const outer = parseIpv4Cidr(range);
  const inner = parseIpv4Cidr(block);
  return outer === null || inner === null || cidrContains(outer, inner);
};

export const CreateNetworkRequest = z
  .object({
    name: NetworkName,
    ipRange: NetworkRange,
    /** Network zone of the first subnet. */
    zone: z.string().trim().min(1),
    /** The first subnet (type cloud); must lie inside `ipRange`. */
    subnetIpRange: Ipv4Block,
    labels: Labels.default({}),
  })
  .refine((v) => insideOrMalformed(v.ipRange, v.subnetIpRange), {
    path: ["subnetIpRange"],
    message: "The subnet must lie inside the network's range",
    params: { reason: "subnet_outside_range" },
  })
  .refine((v) => (parseIpv4Cidr(v.subnetIpRange)?.prefix ?? 0) <= SUBNET_PREFIX_MAX, {
    path: ["subnetIpRange"],
    message: `A subnet can be at most a /${SUBNET_PREFIX_MAX}`,
    params: { reason: "subnet_too_small" },
  });
export type CreateNetworkRequest = z.input<typeof CreateNetworkRequest>;

export const UpdateNetworkRequest = z
  .object({ name: NetworkName.optional(), labels: Labels.optional() })
  .refine((v) => v.name !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdateNetworkRequest = z.infer<typeof UpdateNetworkRequest>;

export const NetworkProtectionRequest = z.object({ delete: z.boolean() });
export type NetworkProtectionRequest = z.infer<typeof NetworkProtectionRequest>;

export const AddSubnetRequest = z.object({ ipRange: Ipv4Block, zone: z.string().trim().min(1) });
export type AddSubnetRequest = z.infer<typeof AddSubnetRequest>;

export const AddRouteRequest = z.object({ destination: Ipv4Block, gateway: Ipv4Address });
export type AddRouteRequest = z.infer<typeof AddRouteRequest>;

export const AttachServerRequest = z.object({
  serverId: z.uuid(),
  /** A fixed address inside a subnet of the server's zone; the provider picks one when absent. */
  ip: Ipv4Address.optional(),
  /** Extra addresses of the server in this network, inside the same subnets. */
  aliasIps: z.array(Ipv4Address).max(MAX_ALIAS_IPS).optional(),
});
export type AttachServerRequest = z.infer<typeof AttachServerRequest>;

export const DetachServerRequest = z.object({ serverId: z.uuid() });
export type DetachServerRequest = z.infer<typeof DetachServerRequest>;
